import { Link } from 'react-router-dom';
import { AlertTriangle, FileCheck2, Send, MailCheck, ExternalLink } from 'lucide-react';
import type { Dossier } from '../SuiviPrivatisationLayout';

const fmtDate = (d?: string | null) => {
    if (!d) return '—';
    try { return new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }); } catch { return '—'; }
};

type Statut = 'perdu' | 'devis_recu' | 'lien_signature' | 'reponse_plaquette';

/** Les situations sont mutuellement exclusives par construction : un dossier ne peut être que dans
 *  l'une d'elles à la fois. */
export const statutDossier = (d: Dossier): Statut | null =>
    d.lost_reason ? 'perdu'
        : (d.devis_signed_received_at && !d.devis_signed_at) ? 'devis_recu'
        : (d.devis_sent_to && !d.devis_signed_received_at && !d.devis_signed_at) ? 'lien_signature'
        : d.plaquette_response ? 'reponse_plaquette'
        : null;

const TON: Record<Statut, { boite: string; icone: string; texte: string }> = {
    perdu: { boite: 'bg-state-danger-soft border-state-danger/30', icone: 'text-state-danger border-state-danger/30', texte: 'text-state-danger' },
    devis_recu: { boite: 'bg-state-ok-soft border-state-ok/30', icone: 'text-state-ok border-state-ok/30', texte: 'text-state-ok' },
    lien_signature: { boite: 'bg-state-info-soft border-state-info/30', icone: 'text-state-info border-state-info/30', texte: 'text-state-info' },
    reponse_plaquette: { boite: 'bg-state-info-soft border-state-info/30', icone: 'text-state-info border-state-info/30', texte: 'text-state-info' },
};

/**
 * Ce que le dossier a à dire en plus de son étape : motif de perte, devis signé déposé par le client,
 * lien de signature en attente, réponse à la plaquette. Un bandeau, sous l'étape en cours, dans le ton
 * de la situation — avant la refonte c'était une carte « Statut » perdue dans la colonne latérale.
 */
export function DossierStatut({ dossier }: { dossier: Dossier }) {
    const statut = statutDossier(dossier);
    if (!statut) return null;
    const t = TON[statut];
    const Icone = statut === 'perdu' ? AlertTriangle : statut === 'devis_recu' ? FileCheck2 : statut === 'lien_signature' ? Send : MailCheck;
    const rep = dossier.plaquette_response;

    return (
        <div className={`flex items-start gap-3 px-[15px] py-3 rounded-xl border ${t.boite}`}>
            <span className={`w-7 h-7 shrink-0 rounded-lg bg-surface border flex items-center justify-center ${t.icone}`}><Icone size={14} /></span>
            <div className="flex-1 min-w-0 text-[12.5px] leading-[1.5] text-pretty">
                {statut === 'perdu' && (
                    <>
                        <div className={`text-[13px] font-bold ${t.texte}`}>Motif de perte</div>
                        <p className="text-content mt-0.5">{dossier.lost_reason}</p>
                        <p className="text-[11px] text-content-muted mt-0.5">Le {fmtDate(dossier.lost_at)}</p>
                    </>
                )}
                {statut === 'devis_recu' && (
                    <>
                        <div className={`text-[13px] font-bold ${t.texte}`}>Devis signé reçu</div>
                        <p className="text-content mt-0.5">Le client l'a déposé le {fmtDate(dossier.devis_signed_received_at)} — son aperçu est dans l'étape en cours, ci-dessus.</p>
                    </>
                )}
                {statut === 'lien_signature' && (
                    <>
                        <div className={`text-[13px] font-bold ${t.texte}`}>Lien de signature envoyé</div>
                        <p className="text-content-muted mt-0.5">
                            À <span className="font-semibold text-content">{dossier.devis_sent_to}</span>
                            {dossier.devis_sent_at ? ` le ${fmtDate(dossier.devis_sent_at)}` : ''}. Le client peut y déposer son
                            exemplaire signé, ou vous dire qu'il ne donne pas suite.
                        </p>
                    </>
                )}
                {statut === 'reponse_plaquette' && rep && (
                    <>
                        <div className={`text-[13px] font-bold ${t.texte}`}>{rep.responded ? 'Le client a répondu à la plaquette' : 'Plaquette envoyée, réponse attendue'}</div>
                        {rep.responded ? (
                            <p className="text-content-muted mt-0.5">
                                Répondu{rep.responded_at ? ` le ${fmtDate(rep.responded_at)}` : ''}.
                                {rep.appointment && (
                                    <Link to="/rdv-pro" className="inline-flex items-center gap-1 ml-1.5 font-semibold text-content hover:underline">
                                        Voir le rendez-vous et ses réponses <ExternalLink size={11} />
                                    </Link>
                                )}
                            </p>
                        ) : (
                            <p className="text-content-muted mt-0.5">
                                Le client n'a pas encore ouvert{rep.template_name ? ` « ${rep.template_name} »` : ' le questionnaire'}.
                            </p>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
